import { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input";
import { Cog6ToothIcon as SettingsIcon, MagnifyingGlassIcon, PlusCircleIcon } from "@heroicons/react/24/outline"
import { CircleStackIcon, RssIcon } from "@heroicons/react/24/solid"
import LocalizationController from "./localization-controller"
import LanguageAddDialog from "./language-add-dialog"
import LanguageDialog from "./language-dialog"
import LocalizationExportDialog from "./localization-export-dialog"
import LocalizationImportDialog from "./localization-import-dialog"
import { usePromise } from "aldoo-ra/usePromise"

export default function LocalizationLanguages() {
  const localizationController = LocalizationController()
  
  const [languages, setLanguages] = useState([])
  const [filteredLanguages, setFilteredLanguages] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [statistics, setStatistics] = useState(null)
  const [openLanguageAddDialog, setOpenLanguageAddDialog] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [openExportDialog, setOpenExportDialog] = useState(false)
  const [openImportDialog, setOpenImportDialog] = useState(false)

  const { result: languageContent, execute: refreshStatistics } = usePromise(
    localizationController.getLanguageStatistics
  )

  useEffect(() => {
    if (!languageContent) return
    localizationController.onRefreshStatistics(() => {
      refreshStatistics()
    })
  }, [languageContent])

  useEffect(() => {
    if (!languageContent) return
    setLanguages(languageContent?.languages)
    setFilteredLanguages(languageContent?.languages)

    //find the primary language and set it to the controller
    const primaryLanguage = languageContent?.languages?.find(
      (language) => language.primary
    )
    localizationController.setPrimaryLanguage(primaryLanguage?.code)
    localizationController.setLanguages(languageContent?.languages)

    //extract the stats
    const { progress, keys } = languageContent
    setStatistics({ progress, keys })
  }, [languageContent])

  useEffect(() => {
    if (!languages) return

    if (!searchQuery.trim()) {
      setFilteredLanguages(languages)
      return
    }

    const query = searchQuery.toLowerCase()
    const filtered = languages.filter((language) =>
      language.name.toLowerCase().includes(query) ||
      language.code.toLowerCase().includes(query)
    )
    setFilteredLanguages(filtered)
  }, [searchQuery, languages])

  return (
    <div className="p-6 bg-admin_canvas min-h-screen">
      {/* Header */}
      <div className="w-full flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6">
        <Typography
          component="h1"
          variant="admin_h3"
          className="whitespace-nowrap"
        >
          Languages Management
        </Typography>

        <div className="w-full xl:w-[65%] flex gap-3 items-center justify-between">
          <Input
            variant="admin"
            placeholder="Search languages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<MagnifyingGlassIcon className="w-6 h-6 text-admin_text_secondary" />}
            className="w-[360px]"
          />

          <div className="flex gap-3 items-center">
            <Button
              variant="admin_secondary"
              onClick={() => setOpenImportDialog(true)}
            >
              Import
            </Button>

            <Button
              variant="admin_secondary"
              onClick={() => setOpenExportDialog(true)}
            >
              Export
            </Button>

            <Button
              variant="admin_primary"
              onClick={() => setOpenLanguageAddDialog(true)}
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Add Language
            </Button>
          </div>
        </div>
      </div>

      {/* Languages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
        {filteredLanguages?.map((language, index) => (
          <div
            key={index}
            className="flex flex-col justify-between bg-admin_paper p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedLanguage(language)}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Typography 
                    variant="admin_h4" 
                    className="text-admin_text font-bold"
                  >
                    {language.name}
                  </Typography>
                  
                  <div
                    className={`w-[10px] h-[10px] rounded-full mt-0.5 ${
                      language.release === true ? "bg-admin_success" : "bg-admin_error"
                    }`}
                  ></div>
                </div>

                <Typography variant="admin_txt_dim">
                  {language.code}
                </Typography>
              </div>
            </div>

            {/* Status badge */}
            <div className="flex flex-wrap gap-2 mb-4">
              {language.primary && (
                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-admin_primary text-admin_text_inv">
                  Primary
                </span>
              )}
            </div>

            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Typography variant="admin_txt2_dim">
                  Progress
                </Typography>

                <Typography variant="admin_txt2" className="text-admin_text font-semibold">
                  {language.progress || 0}%
                </Typography>
              </div>
              
              <div className="w-full h-2 bg-admin_canvas rounded">
                <div
                  className={`bg-${
                    language?.progress == 100 ? "admin_success" : "admin_progress"
                  } h-2 rounded transition-all duration-300`}
                  style={{ width: `${language.progress || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dialogs */}
      <LanguageAddDialog
        controller={localizationController}
        isOpen={openLanguageAddDialog}
        onClose={() => setOpenLanguageAddDialog(false)}
      />

      <LanguageDialog
        controller={localizationController}
        isOpen={selectedLanguage !== null}
        language={selectedLanguage}
        onClose={() => setSelectedLanguage(null)}
      />

      <LocalizationExportDialog
        isOpen={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        controller={localizationController}
      />

      <LocalizationImportDialog
        isOpen={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        controller={localizationController}
      />
    </div>
  )
}